import type { ChakraTheme } from "@chakra-ui/react";
import {
  theme as baseTheme,
  extendTheme,
  withDefaultColorScheme,
} from "@chakra-ui/react";
import { layerStyles } from "theme/layer-styles";
import { textStyles } from "theme/text-styles";
import "theme/default-props";
import { createTinyColor } from "lib/tiny-color";
import { colors, type ClientBrandColors } from "theme/colors";
import { components } from "theme/components";
import { fontSizes } from "theme/font-sizes";
import { fonts } from "theme/fonts";
import { semanticTokens } from "theme/semantic-tokens";
import { shadows } from "theme/shadows";
import { styles } from "theme/styles";

const appTheme = extendTheme(
  {
    ...baseTheme,
    textStyles,
    layerStyles,
    colors,
    components,
    fontSizes,
    shadows,
    styles,
    fonts,
    semanticTokens,
  } as ChakraTheme,
  withDefaultColorScheme({ colorScheme: "brand" }),
);

type GenerateThemeClientConfig = {
  navigationColor?: string;
  accentColor?: string;
  headingFont?: string;
  bodyFont?: string;
};

function generateClientTheme({
  navigationColor,
  accentColor,
  bodyFont,
  headingFont,
}: GenerateThemeClientConfig) {
  const fonts = {} as Record<"heading" | "body", string>;

  if (headingFont) {
    fonts["heading"] = headingFont;
  }

  if (bodyFont) {
    fonts["body"] = bodyFont;
  }

  const client = {} as ClientBrandColors;

  const navigationColorInstance = createTinyColor(navigationColor);
  const accentColorInstance = createTinyColor(accentColor);

  if (navigationColorInstance.isValid) {
    const hoverColorInstance =
      navigationColorInstance.getBrightnessAdjustedTinyColor(4);

    const activeColorInstance =
      navigationColorInstance.getBrightnessAdjustedTinyColor();

    client.navigation = {
      background: navigationColorInstance.value,
      text: {
        primary: navigationColorInstance.contrastColor.value,
        secondary:
          navigationColorInstance.contrastColor.getBrightnessAdjustedTinyColor(
            20,
          ).value,
      },
      hover: {
        background: hoverColorInstance.value,
        text: hoverColorInstance.contrastColor.value,
      },
      active: {
        background: activeColorInstance.value,
        text: activeColorInstance.contrastColor.value,
      },
      divider:
        navigationColorInstance.contrastColor.getTransparentTinyColor(0.3)
          .value,
    };
  }

  if (accentColorInstance.isValid) {
    client.accent = {
      background: accentColorInstance.value,
      text: accentColorInstance.contrastColor.value,
    };
  }

  return extendTheme(appTheme, {
    fonts,
    colors: { client },
  });
}

export { appTheme, generateClientTheme };
